import { cva, type VariantProps } from 'class-variance-authority'
import { AnimatePresence, HTMLMotionProps, motion } from 'framer-motion'
import { forwardRef, ReactNode, useState } from 'react'
import { Link, type LinkProps } from 'react-router-dom'
import { cn } from '@/lib/utils'
import { useHasRedesign } from '@/utils/redesignSellers'

const buttonVariants = cva(
  'group/button inline-flex shrink-0 items-center justify-center gap-1.5 rounded-lg border border-transparent text-sm font-medium whitespace-nowrap transition-all outline-none select-none focus-visible:ring-3 focus-visible:ring-ring/50 active:translate-y-px disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*=\'size-\'])]:size-4',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/80',
        outline: 'border-border bg-background hover:bg-muted hover:text-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-muted hover:text-foreground',
        destructive: 'bg-destructive/10 text-destructive hover:bg-destructive/20',
        link: 'text-primary underline-offset-4 hover:underline'
      },
      size: {
        default: 'h-8 px-2.5',
        xs: 'h-6 rounded-md px-2 text-xs',
        sm: 'h-7 rounded-md px-2.5 text-[0.8rem]',
        lg: 'h-9 px-3',
        icon: 'size-8',
        'icon-xs': 'size-6 rounded-md',
        'icon-sm': 'size-7 rounded-md',
        'icon-lg': 'size-9'
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'default'
    }
  }
)

/* ─── Apariencia anterior ────────────────────────────────────────────────────────────────
   Los sellers que todavía no tienen el rediseño (ver REDESIGN_SELLERS) tienen que seguir
   viendo los botones como los dibujaba NextUI: mismo alto, mismo radio y, sobre todo, la
   misma paleta por color semántico (el verde de "Enviar a bodega", el naranjo de "Reenviar
   código", etc.). En vez de duplicar los componentes que los usan, el botón replica acá ese
   look con las clases que el plugin de NextUI ya publica.

   `legacyColor` / `legacyVariant` existen solo para eso: las pasa el call site cuando el
   botón original no era un `color='primary'` sólido. Si no se pasan, se deducen del variant
   del rediseño. En los sellers con rediseño se ignoran por completo. */

export type LegacyColor = 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'danger'
export type LegacyVariant = 'solid' | 'shadow' | 'bordered' | 'light'

const LEGACY_SOLID: Record<LegacyColor, string> = {
  default: 'bg-default text-default-foreground',
  primary: 'bg-primary text-primary-foreground',
  secondary: 'bg-secondary text-secondary-foreground',
  success: 'bg-success text-success-foreground',
  warning: 'bg-warning text-warning-foreground',
  danger: 'bg-danger text-danger-foreground'
}

const LEGACY_SHADOW: Record<LegacyColor, string> = {
  default: 'bg-default text-default-foreground shadow-lg shadow-default/50',
  primary: 'bg-primary text-primary-foreground shadow-lg shadow-primary/40',
  secondary: 'bg-secondary text-secondary-foreground shadow-lg shadow-secondary/40',
  success: 'bg-success text-success-foreground shadow-lg shadow-success/40',
  warning: 'bg-warning text-warning-foreground shadow-lg shadow-warning/40',
  danger: 'bg-danger text-danger-foreground shadow-lg shadow-danger/40'
}

const LEGACY_BORDERED: Record<LegacyColor, string> = {
  default: 'border-2 border-default text-foreground bg-transparent',
  primary: 'border-2 border-primary text-primary bg-transparent',
  secondary: 'border-2 border-secondary text-secondary bg-transparent',
  success: 'border-2 border-success text-success bg-transparent',
  warning: 'border-2 border-warning text-warning bg-transparent',
  danger: 'border-2 border-danger text-danger bg-transparent'
}

const LEGACY_LIGHT: Record<LegacyColor, string> = {
  default: 'bg-transparent text-foreground hover:bg-default/40',
  primary: 'bg-transparent text-primary hover:bg-primary/20',
  secondary: 'bg-transparent text-secondary hover:bg-secondary/20',
  success: 'bg-transparent text-success hover:bg-success/20',
  warning: 'bg-transparent text-warning hover:bg-warning/20',
  danger: 'bg-transparent text-danger hover:bg-danger/20'
}

const LEGACY_SURFACE: Record<LegacyVariant, Record<LegacyColor, string>> = {
  solid: LEGACY_SOLID,
  shadow: LEGACY_SHADOW,
  bordered: LEGACY_BORDERED,
  light: LEGACY_LIGHT
}

/* Alturas y radios de NextUI por tamaño (sm 32px / md 40px / lg 48px).
   Los tamaños de fuente van con las clases estándar de Tailwind (text-xs/sm/base) y no con
   los alias de NextUI (text-tiny/small/medium): tailwind-merge no los conoce, los toma por
   clases de color y los descarta al chocar con `text-*-foreground`. */
const LEGACY_SIZE: Record<string, string> = {
  default: 'h-10 px-4 text-sm rounded-xl',
  xs: 'h-7 px-2 text-xs rounded-lg',
  sm: 'h-8 px-3 text-xs rounded-lg',
  lg: 'h-12 px-6 text-base rounded-xl',
  icon: 'h-10 w-10 min-w-10 rounded-xl',
  'icon-xs': 'h-7 w-7 min-w-7 rounded-lg',
  'icon-sm': 'h-8 w-8 min-w-8 rounded-lg',
  'icon-lg': 'h-12 w-12 min-w-12 rounded-xl'
}

// Cuando el call site no declara cómo era antes, se deduce del variant del rediseño.
const LEGACY_FALLBACK: Record<string, { color: LegacyColor, variant: LegacyVariant }> = {
  default: { color: 'primary', variant: 'solid' },
  secondary: { color: 'secondary', variant: 'solid' },
  destructive: { color: 'danger', variant: 'solid' },
  outline: { color: 'default', variant: 'bordered' },
  ghost: { color: 'default', variant: 'light' },
  link: { color: 'primary', variant: 'light' }
}

export const legacyButtonClasses = (
  variant: string | null | undefined,
  size: string | null | undefined,
  legacyColor?: LegacyColor,
  legacyVariant?: LegacyVariant
): string => {
  const fallback = LEGACY_FALLBACK[variant ?? 'default'] ?? LEGACY_FALLBACK.default
  const color = legacyColor ?? fallback.color
  const surface = legacyVariant ?? fallback.variant
  return cn(
    'inline-flex shrink-0 items-center justify-center gap-2 font-normal whitespace-nowrap',
    'transition-transform-colors-opacity hover:opacity-80 active:scale-[0.97] outline-none',
    'disabled:opacity-50 disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:shrink-0',
    LEGACY_SIZE[size ?? 'default'] ?? LEGACY_SIZE.default,
    LEGACY_SURFACE[surface][color]
  )
}

const ShimmerSweep = ({ show }: { show: boolean }) => (
  <AnimatePresence>
    {show && (
      <motion.span
        key='shimmer'
        initial={{ x: '-150%' }}
        animate={{ x: '250%' }}
        exit={{}}
        transition={{ duration: 0.65, ease: 'easeInOut' }}
        className='absolute inset-0 w-1/3 pointer-events-none'
        style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)', skewX: '-15deg' }}
      />
    )}
  </AnimatePresence>
)

export interface ButtonProps extends Omit<HTMLMotionProps<'button'>, 'ref' | 'children'>, VariantProps<typeof buttonVariants> {
  shimmer?: boolean
  children?: ReactNode
  /* Cómo se veía este botón antes del rediseño. Solo aplica a los sellers sin rediseño. */
  legacyColor?: LegacyColor
  legacyVariant?: LegacyVariant
  /* Clases extra que solo valen en la apariencia anterior (ej. el `text-white` que varios
     botones naranjos/verdes llevaban encima del color de NextUI). */
  legacyClassName?: string
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, shimmer = true, disabled, children, legacyColor, legacyVariant, legacyClassName, ...props }, ref) => {
    const [hovering, setHovering] = useState(false)
    const hasRedesign = useHasRedesign()

    if (!hasRedesign) {
      return (
        <motion.button
          ref={ref}
          data-slot='button'
          disabled={disabled}
          className={cn(legacyButtonClasses(variant, size, legacyColor, legacyVariant), className, legacyClassName)}
          {...props}
        >
          {children}
        </motion.button>
      )
    }

    return (
      <motion.button
        ref={ref}
        data-slot='button'
        disabled={disabled}
        onHoverStart={() => { if (shimmer && !disabled) setHovering(true) }}
        onHoverEnd={() => setHovering(false)}
        className={cn(buttonVariants({ variant, size, className }), 'relative overflow-hidden')}
        {...props}
      >
        <ShimmerSweep show={Boolean(shimmer && hovering)} />
        {children}
      </motion.button>
    )
  }
)
Button.displayName = 'Button'

const MotionLink = motion(Link)

type ConflictingLinkHandlers = 'onAnimationStart' | 'onAnimationEnd' | 'onDrag' | 'onDragStart' | 'onDragEnd'

export interface ButtonLinkProps extends Omit<LinkProps, 'className' | ConflictingLinkHandlers>, VariantProps<typeof buttonVariants> {
  shimmer?: boolean
  className?: string
}

export const ButtonLink = forwardRef<HTMLAnchorElement, ButtonLinkProps>(
  ({ className, variant, size, shimmer = true, children, ...props }, ref) => {
    const [hovering, setHovering] = useState(false)

    return (
      <MotionLink
        ref={ref}
        data-slot='button'
        onHoverStart={() => { if (shimmer) setHovering(true) }}
        onHoverEnd={() => setHovering(false)}
        className={cn(buttonVariants({ variant, size, className }), 'relative overflow-hidden')}
        {...props}
      >
        <ShimmerSweep show={Boolean(shimmer && hovering)} />
        {children}
      </MotionLink>
    )
  }
)
ButtonLink.displayName = 'ButtonLink'

/* Mismas clases que `<Button>`, para los casos en que el botón no es un <button> sino un
   <a> o un <span> (descargar contrato, adjuntar imagen). Respeta la apariencia del seller. */
export const useButtonClasses = () => {
  const hasRedesign = useHasRedesign()
  return ({ variant, size, legacyColor, legacyVariant }: {
    variant?: ButtonProps['variant']
    size?: ButtonProps['size']
    legacyColor?: LegacyColor
    legacyVariant?: LegacyVariant
  } = {}): string => hasRedesign
    ? buttonVariants({ variant, size })
    : legacyButtonClasses(variant, size, legacyColor, legacyVariant)
}

export { buttonVariants }
